import { useApolloClient } from '@apollo/client'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { ShiftManagementMainPageDocument } from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import getMultiFilters from 'modules/core/components/MultiFilters/hooks/utils/getMultiFilters'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/lib/mobx-rest'

import {
  formatCustomFilters,
  getCustomFilters,
} from 'modules/shift_management/components/ShiftManagement/Content/_filters/customFilters'
import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import useApplyFilters from 'modules/shift_management/hooks/useApplyFilters'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import { CopyScheduleResponse, Filters } from 'modules/shift_management/lib/types'
import RestDay, { RestDayPayload } from 'modules/shift_management/store/internal/models/RestDay'
import type { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'

interface Props {
  originalView: View
  targetView: View
}

// TODO: migrate to GraphQL mutation bulkCopyShiftmanagementSchedule
// when the backend returns shift and rest days in proper way to GraphQL (not entities)
const copySchedule = async (
  originalStartAt: string,
  targetStartAt: string,
  filters: Filters
): Promise<CopyScheduleResponse> => {
  const api = apiClient()

  // Send days_to_copy to 7, 28 or the month's days to copy based on view (week, 4weeks, month)
  return await api.post('/shift_management/schedules/bulk_copy', {
    original_start_at: originalStartAt,
    target_start_at: targetStartAt,
    ...filters,
  })
}

const useCopySchedule = ({ originalView, targetView }: Props) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchUI] = useUI()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newMultiFiltersEnabled = hasFeatureFlag('dev_shift_management_new_multi_filters')

  const { refreshStats } = useRefreshStats()
  const apolloClient = useApolloClient()

  const { getFilteredEmployees } = useApplyFilters(shiftsRows)
  const [{ employeeIds, teamIds, legalEntityIds, customFilters }] = useFilters()

  const { running: submitting, performAction: onConfirm } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const employeeIdsOld = getFilteredEmployees().map((e) => e.id)
      const employees = employeeIds?.length ? employeeIds.map(Number) : []

      const { filters: multiFilters, logicOperator } = getMultiFilters(
        LOCAL_STORAGE_KEYS.shiftEmployees
      )

      const customFiltersOld = getCustomFilters(multiFilters)

      // TODO: Migrate to GraphQL mutations
      const result = await asyncAction(
        () =>
          copySchedule(originalView.startAt, targetView.startAt, {
            employee_ids: newMultiFiltersEnabled ? employees : employeeIdsOld,
            logic_operator: newMultiFiltersEnabled
              ? LOGIC_OPERATOR.AND
              : logicOperator.get('logic_operator').value,
            custom_filters: newMultiFiltersEnabled
              ? customFilters
              : formatCustomFilters(customFiltersOld),
            team_ids: teamIds,
            legal_entity_ids: legalEntityIds,
          }),
        {
          successFlash: true,
          errorFlash: true,
        }
      )

      if (isSuccessful(result)) {
        // TODO: Actually update the cache properly
        await apolloClient.refetchQueries({ include: [ShiftManagementMainPageDocument] })
        const { value: schedule } = result
        const copiedShifts = schedule.shifts.map((shift: ShiftPayload) =>
          Shift.buildFromRequest(shift)
        )
        const copiedRestDays = schedule.rest_days.map((restDay: RestDayPayload) =>
          RestDay.build(restDay)
        )
        const impactedEmployees: number[] = uniq(copiedShifts.map((s: Shift) => s.employeeId))
        const hexCodes = copiedShifts.map((shift: Shift) => shift.color)
        const uniqHexCodes = uniq(hexCodes)
        const color = uniqHexCodes.map((code) => (code ? nameFromHexCode(code) : null))

        trackAddShiftSubmitEvent({
          view,
          employeeIds: impactedEmployees,
          shiftsCount: copiedShifts.length,
          restDaysCount: copiedRestDays.length,
          shiftType: 'copyPeriod',
          color,
          from: 'copy_period',
        })

        dispatchUI({ type: 'LOADING' })
        dispatchSchedule({ type: 'RESET_VIEW', view })
        dispatchPagination({ type: 'RESET_PAGINATION' })

        dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts: copiedShifts, view })
        dispatchShiftsRows({ type: 'ADD_REST_DAYS', restDays: copiedRestDays })

        await refreshStats(shiftsRows)
      }

      closePortal()
    },
    {
      successMessage: i18n.t('shift_management.actions.copy_shifts_success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    onConfirm,
  }
}

export default useCopySchedule
